import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { useLogHomeHealthEvent } from "../../lib/useLogHomeHealthEvent";
import {
  type Case,
  caseResponseSchema,
  CaseStatus,
  type CreateCaseRequest,
  createCaseRequestSchema,
} from "../types";
import { getAgencyCasesQueryKey } from "./useAgencyCases";
import { getCaseQueryKey } from "./useCase";

export function useCreateCase(options: UseMutationOptions<Case, string, CreateCaseRequest> = {}) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";
  const isAdmin = useIsAdmin();
  const logHomeHealthEvent = useLogHomeHealthEvent();

  return useMutation({
    mutationFn: async (params) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/${workplaceId}/cases`,
        data: params,
        requestSchema: createCaseRequestSchema,
        responseSchema: caseResponseSchema,
      });

      return response.data.data;
    },
    onSuccess: (newCase) => {
      logHomeHealthEvent(APP_V2_USER_EVENTS.HOME_HEALTH_CASE_CREATED, {
        workplaceId,
        caseId: newCase.id,
        patientId: newCase.attributes.patient.id,
        performedByAdmin: isAdmin,
      });
      queryClient.setQueryData(getCaseQueryKey({ workplaceId, caseId: newCase.id }), newCase);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(getCaseQueryKey({ workplaceId, caseId: newCase.id }));
      queryClient.setQueryData(
        getAgencyCasesQueryKey({ workplaceId, filter: { status: CaseStatus.OPEN } }),
        (oldCases?: Case[]) =>
          // eslint-disable-next-line unicorn/prefer-spread
          (oldCases ?? []).concat(newCase)
      );
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(
        getAgencyCasesQueryKey({ workplaceId, filter: { status: CaseStatus.OPEN } })
      );
    },
    ...options,
  });
}
